import { InputHTMLAttributes, forwardRef, useState } from "react";
import { MdVisibility, MdVisibilityOff } from "react-icons/md";
import { InputMask } from "@react-input/mask";

interface InputFieldProps extends InputHTMLAttributes<HTMLInputElement> {
  id: string;
  extra?: string;
  extraContainer?: string;
  variant?: "solid" | "outline";
  state?: "error" | "success"; // Specific states for styling
  readonly?: boolean;
  sizes?: "lg" | "md" | "sm" | "xs";
  onChange?: (val: any) => void;
  color?: "primary" | "secondary" | "white";
  rounded?: string;
  loading?: boolean;
  secure?: boolean; // New prop to indicate password/secure input
  mask?: string; // Mask pattern for @react-input/mask
  replacement?: Record<string, RegExp>; // Replacement patterns for @react-input/mask
  showMask?: boolean; // Show mask characters
  separate?: boolean; // Separate mask characters
  lazy?: boolean; // Lazy mask
  eager?: boolean; // Eager mask
  overlap?: boolean; // Overlap mask
  onMask?: (value: string, mask: string) => void; // Callback when mask is applied
}

const MaskInput = forwardRef<HTMLInputElement, InputFieldProps>(
  (
    {
      id,
      value,
      type = "text",
      placeholder,
      sizes = "lg",
      variant = "outline",
      color = "primary",
      rounded = "rounded-2xl",
      state,
      disabled,
      readonly,
      loading = false,
      extra = "",
      extraContainer = "",
      onChange,
      onBlur,
      onFocus,
      secure = false,
      mask,
      replacement,
      showMask = false,
      separate = false,
      lazy = false,
      eager = false,
      overlap = false,
      onMask,
      ...rest
    },
    ref
  ) => {
    const isDisabled = disabled || loading;
    const [showPassword, setShowPassword] = useState(false);
    const isPasswordType = secure || type === "password";
    const inputType = isPasswordType ? (showPassword ? "text" : "password") : type;

    const colorClasses = {
      primary: {
        solid: `text-primary bg-gray-50 placeholder:text-gray-500`,
        outline: `border border-primary text-primary bg-transparent focus:bg-white placeholder:text-gray-500`,
      },
      secondary: {
        solid: `text-secondary bg-gray-50 placeholder:text-gray-500`,
        outline: `border border-secondary text-secondary bg-transparent focus:bg-white placeholder:text-gray-500`,
      },
      white: {
        solid: `text-primary bg-white placeholder:text-gray-500`,
        outline: `border border-white text-primary bg-transparent focus:bg-white placeholder:text-gray-500`,
      },
    };

    const sizeClasses = {
      lg: "h-12 px-6 text-txt-lg",
      md: "h-10 px-4 text-txt-md",
      sm: "h-8 px-3 text-txt-sm",
      xs: "h-6 px-2 text-txt-xs",
    };

    const stateClasses = {
      error: "outline-red-500 text-red-500 placeholder:text-red-500",
      success: "outline-green-500 text-green-500 placeholder:text-green-500",
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const inputValue = e.target.value;
      const parsedValue =
        type === "number" && !isNaN(Number(inputValue))
          ? parseFloat(inputValue)
          : inputValue;

      onChange?.(parsedValue);
    };

    const inputClassName = `flex w-full items-center justify-center ${rounded} ${
      sizeClasses[sizes]
    } ${colorClasses[color][variant]} ${
      state ? stateClasses[state] : "outline-none"
    } ${
      isDisabled
        ? "border-none bg-gray-50/50"
        : "hover:opacity-50 focus:opacity-100"
    } ${extra}`;

    // If mask is provided, use InputMask component
    if (mask) {
      return (
        <div className={`${extraContainer} flex w-full relative`}>
          <InputMask
            ref={ref}
            mask={mask}
            replacement={replacement}
            showMask={showMask}
            separate={separate}
            lazy={lazy}
            eager={eager}
            overlap={overlap}
            onMask={onMask}
            component="input"
            type={inputType}
            value={value !== undefined ? String(value) : ""}
            placeholder={readonly ? "" : placeholder}
            readOnly={readonly}
            disabled={isDisabled}
            onChange={handleChange}
            onFocus={onFocus}
            onBlur={onBlur}
            className={inputClassName}
            {...rest}
          />
          {isPasswordType && (
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
            >
              {showPassword ? <MdVisibilityOff size={20} /> : <MdVisibility size={20} />}
            </button>
          )}
        </div>
      );
    }

    // If no mask is provided, use regular input (fallback to original behavior)
    return (
      <div className={`${extraContainer} flex w-full relative`}>
        <input
          {...rest}
          ref={ref}
          id={id}
          type={inputType}
          value={value !== undefined ? String(value) : ""}
          placeholder={readonly ? "" : placeholder}
          readOnly={readonly}
          disabled={isDisabled}
          onChange={handleChange}
          onFocus={onFocus}
          onBlur={onBlur}
          className={inputClassName}
        />
        {isPasswordType && (
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
          >
            {showPassword ? <MdVisibilityOff size={20} /> : <MdVisibility size={20} />}
          </button>
        )}
      </div>
    );
  }
);

export default MaskInput;

